import $ from 'jquery'
import _ from 'underscore'
import productChannel, { ProductOptionsChanged } from '@/catalog/productChannel'
import { urlParams } from '@/utils/url'
import { itemSurfaceUrl } from '@/api/mondrian/surface'

interface ProductPageOptions {
  quantity: number
  substrate?: string
  thickness?: string
  trimPrinterCode?: string
  trimEnumName?: string
  trim?: string
}

/**
 * Mutable quantity to track the current state.
 *
 * TODO: Track this state in a cleaner way, like by using a model, or even a model that's already being created.
 */
let quantity: number = NaN

/**
 * Retrieves the current quantity of the main item on the product page.
 * This may be mutated as the user selects a different quantity, or via the hash in the URL.
 */
export const getQuantity = () => {
  if (isNaN(quantity)) {
    // In the absence of cleaner init strategy, prevent mistakes as long as the getter is used
    throw new Error('Options must be initialized before getting quantity')
  }
  return quantity
}

// Inlined item will always exist via product.jade
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const item = window.item!
const isFramedPrint =
  (typeof item.format.itemType === 'object'
    ? item.format.itemType.value
    : item.format.itemType) === 'Framed Print'

/**
 * Builds an options object from the currently selected input values in the DOM.
 */
export const getOptions = (): ProductPageOptions => {
  const options: ProductPageOptions = {
    quantity: getQuantity()
  }

  const $substrate = $('select[name=substrate] option:selected')
  if ($substrate.length) {
    options.substrate = $substrate.val() as string
    // Friendly name for URL
    options.thickness = $substrate.data('thickness')
  }

  const $trim = $('.trim-container.selected')
  if ($trim.length) {
    options.trimPrinterCode = $trim.data('trimPrinterCode') as string
    options.trimEnumName = $trim.data('trimEnumName')
    // Friendly name for URL
    options.trim = options.trimPrinterCode.toLowerCase()
  }

  return options
}

/**
 * Strips options (e.g. `#optionA=x&optionB=y`) from the given URL string.
 *
 * This assumes that the options to be removed are located at the end of the given URL string, after the `#`.
 */
export const withoutOptionsHash = (
  href: string // Assumes that options are at the end of the string
) => href.split('#')[0]

export const getOptionsHash = function () {
  const options = _.omit(
    getOptions(),
    'trimPrinterCode',
    'trimEnumName',
    'substrate'
  )
  return '#' + $.param(options)
}

/**
 * Updates the location hash in the URL to reflect the current application state.
 */
const updateLocationHash = () => {
  const optionsHash = getOptionsHash()
  if (optionsHash !== location.hash) {
    location.replace(optionsHash)
  }
}

/**
 * When going to a related-products page (format, color, "We Also Suggest"),
 * Add the currently chosen options (quantity, substrate, trim) to the URL before navigating away.
 *
 * The chosen format and color are implicitly defined by the current item ID.
 */
const passOptionsToRelatedItemClicks = () => {
  $(
    '.related-products a, .product-formats a, .product-color-icons a, .product-occasions a, .product-options a'
  ).on('click', function () {
    const $this = $(this)
    const hrefWithoutOptions = withoutOptionsHash($this.attr('href')!)
    const url = hrefWithoutOptions + getOptionsHash()
    // We are just setting the href before going to the href
    $this.attr('href', url)
  })
}

/**
 * Updates the hero image for a product based on the selected trim.
 *
 * This uses the v3 item surface rendering API to dynamically rerender a flat surface with the selected trim.
 * It also supports rendering at a higher resolution than the default catalog images for most products.
 *
 * Initially, this is only usable with Framed Prints, which are the first items to have merch images
 * embedded in their item surface var data.
 *
 * TODO: Expand usage to additional products once merch image data is available.
 */
const updateHeroImageFromTrim = function () {
  const trim = getOptions().trimEnumName!
  const newMerchImageUrl = itemSurfaceUrl({
    item,
    // 607 is the max size in the responsive design for common viewports.
    // 610 is due to rounding up to the nearest 10 to complement imgix.js 2.x fluid logic.
    size: 610,
    dpr: 3,
    fulfillmentAttributes: { trim }
  })
  const prodHeroImg = $('responsive-image.prod-hero-img:first')[0]!
  prodHeroImg.setAttribute('src', newMerchImageUrl)
}

const updateTrimSelections = function (trim: string) {
  const trimString = trim.toUpperCase()
  const $trimToSelect = $(
    `.trim-set:visible .trim-container[data-trim-printer-code=${trimString}]`
  )

  // If we don't find the trim we're looking for on this page
  // skip selecting it and leave it as rendered by the server.
  if ($trimToSelect.length != 0) {
    $('.trim-container').removeClass('selected')
    $('.trim-image').removeClass('active')

    $trimToSelect
      .addClass('selected')
      .find(`.trim-image[data-trim-printer-code=${trimString}]`)
      .addClass('active')
    updateLocationHash()
  }
}

export const initializeOptions = () => {
  // If we change quantity, substrate, or trim, update the URL hash accordingly.
  // The view which updates the DOM should trigger this event after DOM modification is complete and ready to be read.
  productChannel.on('optionChanged', (changes: ProductOptionsChanged) => {
    updateLocationHash()
    if ('trim' in changes && isFramedPrint) {
      updateHeroImageFromTrim()
    }
  })

  // Load chosen options if present in URL
  // Do these after loading the above events, so that the above events can take care of us if necessary.

  // Load chosen options if present in URL: quantity
  const productPageParams = urlParams(location.hash.substring(1))
  if (productPageParams.quantity) {
    quantity = parseInt(productPageParams.quantity)
  }
  const $addToCartQuantitySelect = $('#addtocart-quantity')
  const updateQuantityFromSelect = () =>
    (quantity = parseInt($addToCartQuantitySelect.val() as string))
  if (quantity) {
    // Update DOM with quantity set via page params or settings
    $addToCartQuantitySelect.val(quantity).trigger('change')
    // Update hash to retain options on refresh or history change
    updateLocationHash()
  } else {
    // Update quantity variable from default in DOM
    updateQuantityFromSelect()
  }
  // Keep quantity variable updated
  $addToCartQuantitySelect.on('change', updateQuantityFromSelect)

  // Load chosen options if present in URL: substrate
  // Note that we are calling it 'thickness' in the URL, but keeping it 'substrate' internally
  if (productPageParams.thickness) {
    const $dropdownSubstrate = $('select[name=substrate]')
    if ($dropdownSubstrate.length) {
      const $thicknessOption = $dropdownSubstrate.find(
        'option[data-thickness=' + productPageParams.thickness + ']'
      )
      if ($thicknessOption.length < 1) {
        // We can't find this thickness within our substrate dropdown.
        // Maybe the data is incomplete, or a bad parameter made it into the URL.
      } else {
        $dropdownSubstrate.val($thicknessOption.val() as string)
      }
    }
    updateLocationHash()
  }

  // Load chosen options if present in URL: trim
  if (productPageParams.trim) {
    updateTrimSelections(productPageParams.trim)
  }

  passOptionsToRelatedItemClicks()
}
